import { Box } from '@material-ui/core';
import { Alert } from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more.js';
import Highcharts from 'highcharts/highstock';
import xRange from 'highcharts/modules/xrange.js';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../../../utils/colors';
import { GraphResponseTimeInfo } from './GraphResponseTimeInfo';
import { useObjectivesList } from '../../../hooks/useObjectivesList';

highchartsMore(Highcharts);
xRange(Highcharts);

export function ResponseTimeForNode({ externalData }) {
  const chartResponseTimeForNodeRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const { t } = useTranslation();
  const [hoverData, setHoverData] = React.useState(null);
  const { data: objectives } = useObjectivesList({ fromStorage: true });

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      if (chartResponseTimeForNodeRef.current) {
        chartResponseTimeForNodeRef.current.chart.reflow();
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.unobserve(container);
    };
  }, []);

  function onHoverData(e) {
    const { x, y, series } = e;
    let point = [];
    point['x'] = x;
    point['y'] = y;
    let step = series.userOptions.name;
    setHoverData({ ...point, step });
  }

  const processData = (externalData) => {
    let arrayChartData = [];
    const colors = [
      '#2199e8',
      '#fed12d',
      '#ff7373',
      '#ccff00',
      '#b6fcd5',
      '#065535',
      '#ffc0cb',
      '#008080',
      '#00ffff',
      '#ffa500',
      '#800000',
      '#fff68f',
      '#bada55',
      '#8a2be2',
      '#3b5998',
    ];

    let sla_rendimiento_error = 0;
    let sla_rendimiento_ok = 90;

    if (objectives) {
      if (externalData.Objetivo.Objetivo_Id) {
        const currentObj = objectives.find(
          (obj) =>
            parseInt(obj.objetivo_id) === parseInt(externalData.Objetivo.Objetivo_Id)
        );
        if (currentObj) {
          sla_rendimiento_error = parseFloat(currentObj.sla_rendimiento_error);
          sla_rendimiento_ok = parseFloat(currentObj.sla_rendimiento_ok);
        }
      }
    }

    let chartTitle = '';

    // This is a simplified check based on typical prop structure, but 'schedule' was a local variable in original logic,
    // usually passed as arg. Here we rely on `externalData.Objetivo.Paso` structure to infer if there is data.
    if (externalData.Objetivo.Paso[0] && externalData.Objetivo.Paso[0].estado) {
      const agrupadoISP = {};
      const dataEstadoPasos = externalData.Objetivo.Paso.flatMap((item) =>
        item['estado'].flatMap((j) => j['rendimientoDetalle'])
      );
      dataEstadoPasos.forEach((item) => {
        if (item.nodo_nombre in agrupadoISP) {
          agrupadoISP[item.nodo_nombre].estadoDetalle = [
            ...agrupadoISP[item.nodo_nombre].estadoDetalle,
            ...item.estadoDetalle,
          ];
        } else {
          agrupadoISP[item.nodo_nombre] = {
            nodo_id: item.nodo_id,
            nodo_nombre: item.nodo_nombre,
            estadoDetalle: item.estadoDetalle,
          };
        }
      });
      Object.values(agrupadoISP).forEach((j, forEachKey) => {
        let chartDataSeries = {};
        let arrayData = [];
        j.estadoDetalle.forEach((item) => {
          var dict = {};
          let date = new Date(item.inicio);
          let year = date.getFullYear();
          let month = date.getMonth();
          let day = date.getDate();
          let hours = date.getHours();
          let minutes = date.getMinutes();
          let seconds = date.getSeconds();
          dict['x'] = Date.UTC(year, month, day, hours, minutes, seconds);
          dict['y'] = item.respuesta;
          arrayData.push(dict);
        });
        chartDataSeries['name'] = j.nodo_nombre;
        chartDataSeries['color'] = colors[forEachKey];
        chartDataSeries['data'] = arrayData;
        arrayChartData.push(chartDataSeries);
      });
      // Try to set title if data exists
      if (externalData.Objetivo.Paso[0].paso_nombre) {
        chartTitle = externalData.Objetivo.Paso[0].paso_nombre;
      }
    } else {
      Object.keys(externalData.Objetivo.Paso).forEach((Objectivekey) => {
        const paso = externalData.Objetivo.Paso[Objectivekey];
        if (paso.paso_nombre) {
          chartTitle = paso.paso_nombre;
        }

        Object.keys(paso.rendimientoDetalle).forEach((key) => {
          let chartDataSeries = {};
          let arrayData = [];
          paso.rendimientoDetalle[key].estadoDetalle.forEach((item) => {
            var dict = {};
            let date = new Date(item.inicio);
            let year = date.getFullYear();
            let month = date.getMonth();
            let day = date.getDate();
            let hours = date.getHours();
            let minutes = date.getMinutes();
            let seconds = date.getSeconds();
            dict['x'] = Date.UTC(year, month, day, hours, minutes, seconds);
            dict['y'] = item.respuesta;
            arrayData.push(dict);
          });
          chartDataSeries['name'] = paso.rendimientoDetalle[key].nodo_nombre;
          chartDataSeries['color'] = colors[key];
          chartDataSeries['data'] = arrayData;
          arrayChartData.push(chartDataSeries);
        });
      });
    }

    const arrayChartDataMarked = externalData.Objetivo.Peridos_Marcados
      ? externalData.Objetivo.Peridos_Marcados.map((item) => {
          let chartDataSeries = {};
          if (item.id_tipo_marcado === 7) {
            chartDataSeries['from'] = '';
            chartDataSeries['to'] = '';
            chartDataSeries['color'] = '';
            chartDataSeries['zIndex'] = 1;
          }
          if (item.id_tipo_marcado === 9) {
            let dateStart = new Date(item.fecha_inicio);
            let dateEnd = new Date(item.fecha_termino);
            chartDataSeries['from'] = Date.UTC(
              dateStart.getFullYear(),
              dateStart.getMonth(),
              dateStart.getDate(),
              dateStart.getHours(),
              dateStart.getMinutes(),
              dateStart.getSeconds()
            );
            chartDataSeries['to'] = Date.UTC(
              dateEnd.getFullYear(),
              dateEnd.getMonth(),
              dateEnd.getDate(),
              dateEnd.getHours(),
              dateEnd.getMinutes(),
              dateEnd.getSeconds()
            );
            chartDataSeries['color'] = COLORS.EVENTO_CLIENTE;
            chartDataSeries['zIndex'] = 1;
          }
          if (item.id_tipo_marcado === 10) {
            let dateStart = new Date(item.fecha_inicio);
            let dateEnd = new Date(item.fecha_termino);
            chartDataSeries['from'] = Date.UTC(
              dateStart.getFullYear(),
              dateStart.getMonth(),
              dateStart.getDate(),
              dateStart.getHours(),
              dateStart.getMinutes(),
              dateStart.getSeconds()
            );
            chartDataSeries['to'] = Date.UTC(
              dateEnd.getFullYear(),
              dateEnd.getMonth(),
              dateEnd.getDate(),
              dateEnd.getHours(),
              dateEnd.getMinutes(),
              dateEnd.getSeconds()
            );
            chartDataSeries['color'] = COLORS.MARCADO_ATENTUS;
            chartDataSeries['zIndex'] = 1;
          }
          return chartDataSeries;
        })
      : [];

    return {
      chart: {
        type: 'spline',
        zoomType: 'x',
        ignoreHiddenSeries: false,
        marginRight: 30,
        resetZoomButton: {
          theme: {
            fill: 'white',
            stroke: 'silver',
            r: 0,
            states: {
              hover: {
                fill: '#41739D',
                style: {
                  color: 'white',
                },
              },
            },
          },
        },
      },
      title: {
        text: '',
        style: {
          fontSize: '',
        },
      },
      subtitle: {
        text: '',
        style: {
          color: '',
          fontWeight: '',
        },
      },
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          second: '%l:%M:%S%P',
          minute: '%l:%M%P',
          hour: '%H:%M',
          day: '<br/><b>%e/%b</b>',
          week: '%e. %b',
          month: '%b %y',
          year: '%Y',
        },
        plotBands: arrayChartDataMarked,
      },
      yAxis: {
        min: 0,
        title: {
          text: t('response-time-for-node.graph.title'),
        },
        plotLines: [
          {
            value: sla_rendimiento_ok,
            color: '#54a51c',
            width: 2,
            zIndex: 3,
          },
          {
            value: sla_rendimiento_error,
            color: '#D22129',
            width: 2,
            zIndex: 3,
          },
        ],
      },
      plotOptions: {
        series: {
          gapSize: 2,
          marker: {
            symbol: 'circle',
            width: 8,
            height: 8,
            enabled: false,
          },
          point: {
            events: {
              mouseOver: function (event) {
                onHoverData(this);
              },
            },
          },
        },
      },
      legend: {
        enabled: true,
        useHTML: true,
        symbolWidth: 0,
        labelFormatter: function () {
          let symbol =
            '<div style="background:' +
            this.color +
            ';width:15px;height:3px;margin-top:5px"></div>';
          let name =
            '<button style="padding-left:4px;border-width:0px;background:white">' +
            this.name +
            '</button>';
          return (
            '<div style="display: flex;flex-direction: row;flex-wrap: nowrap;justify-content: space-between;">' +
            symbol +
            ' ' +
            name +
            '</div>'
          );
        },
        title: {
          text: chartTitle,
        },
      },
      credits: {
        enabled: false,
      },
      navigator: {
        enabled: null,
      },
      tooltip: {
        enabled: false,
        xDateFormat: '%Y-%m-%d',
        pointFormat: ` ${t(
          'response-time-for-node.graph.tooltip.point-format'
        )}: <b>{point.y:.2f} (s)</b>`,
      },
      series: arrayChartData,
    };
  };

  if (!externalData || Object.keys(externalData).length === 0) {
    return <Alert severity="info">{t('text.no-data')}</Alert>;
  }

  const chartOptions = processData(externalData);

  if (!chartOptions) {
    return <Alert severity="info">{t('text.no-data')}</Alert>;
  }

  return (
    <Box ref={containerRef} width="100%">
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        ref={chartResponseTimeForNodeRef}
        containerProps={{ style: { width: '100%' } }}
      />
      <GraphResponseTimeInfo {...hoverData} />
    </Box>
  );
}

export default ResponseTimeForNode;
